/*
 * PEARSON PROPRIETARY AND CONFIDENTIAL INFORMATION SUBJECT TO NDA
 * Copyright © 2026 Pearson Education, Inc.
 * All Rights Reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Pearson Education, Inc.  The intellectual and technical concepts contained
 * herein are proprietary to Pearson Education, Inc. and may be covered by U.S. and Foreign Patents,
 * patent applications, and are protected by trade secret or copyright law.
 * Dissemination of this information, reproduction of this material, and copying or distribution of this software
 * is strictly forbidden unless prior written permission is obtained
 * from Pearson Education, Inc.
 */

/**
 * @file advancedSettingsUtils.js - Pure helpers for Advanced Settings param merge/strip logic.
 */

import { ENABLE_AGENT_THINKING_MSG_KEY, ENABLE_MEMORY_SAVER_KEY } from '../constants/StudioTextConstants';
import { findVoiceInConfig } from '../hooks/fetchAudioOptionsConfig';
import utils from '../../../common/utils';
import {
  getGraphAgentThinkingFromParams,
  getGraphMemorySaverFromParams,
  resolveAgentThinkingSaveValue,
  resolveMemorySaverSaveValue
} from './featureConfigUtils';

export const EMPTY_VOICE = { name: '', type: '', language: '' };

export const stripEnableMemorySaver = (params) => {
  if (!params || typeof params !== 'object') {
    return {};
  }
  const { enableMemorySaver, ...rest } = params;
  return rest;
};

export const stripEnableAgentThinkingMsg = (params) => {
  if (!params || typeof params !== 'object') {
    return {};
  }
  const { enableAgentThinkingMsg, ...rest } = params;
  return rest;
};

const getSelectedVoiceFromParams = (source) => {
  const { voice } = source || {};
  if (Array.isArray(voice) && voice.length > 0) {
    return voice[0];
  }
  if (voice && typeof voice === 'object' && voice.name) {
    return voice;
  }
  return {};
};

const getAudioSourceFromParams = (params) => {
  if (params?.audioOptions && typeof params.audioOptions === 'object') {
    return { ...params, ...params.audioOptions };
  }
  return params || {};
};

export const getAudioOptionsFromParams = (params) => {
  const source = getAudioSourceFromParams(params);
  const voice = getSelectedVoiceFromParams(source);
  return {
    audioEnabled: source.audioEnabled ?? source.enabled ?? false,
    providerId: source.providerId || source.provider || '',
    audioModelName: source.audioModelName || source.audioModel || source.model || '',
    voiceName: voice.name || ''
  };
};

const buildAudioConfigFields = (audioEnabled, providerId, audioModelName, voice) => ({
  audioEnabled,
  providerId,
  audioModelName,
  voice: {
    name: voice.name,
    type: voice.type,
    language: voice.language
  }
});

/** Additional Params JSON editor — excludes audio fields managed by the Audio Options form */
export const stripAudioFromParams = (params) => {
  const {
    audioOptions,
    audioEnabled: _audioEnabled,
    providerId: _providerId,
    audioModelName: _audioModelName,
    audioModel: _audioModel,
    model: _model,
    provider: _provider,
    enabled: _enabled,
    voice: _voice,
    ...rest
  } = params || {};

  return rest;
};

export const mergeAudioIntoParams = (params, audioEnabled, providerId, audioModelName, voice) => {
  const rest = stripAudioFromParams(params || {});

  if (!audioEnabled) {
    return {
      ...rest,
      audioEnabled: false
    };
  }

  return {
    ...rest,
    ...buildAudioConfigFields(audioEnabled, providerId, audioModelName, voice)
  };
};

export const isAudioSelectionComplete = (providerId, model, voiceName) => Boolean(providerId && model && voiceName);

export const getAdditionalParamsJsonText = (otherParams) => {
  const additionalParams = stripEnableAgentThinkingMsg(stripEnableMemorySaver(stripAudioFromParams(otherParams || {})));
  return JSON.stringify(additionalParams, null, 2);
};

/**
 * Resolves voice object for save from Advanced Settings audio selections.
 * @param {object} settingsState
 * @param {Array} audioProviderConfig
 * @returns {{ voice: object, valid: boolean }}
 */
export const resolveVoiceForSave = (settingsState, audioProviderConfig) => {
  const { audioEnabled, selectedProviderId, selectedModel, selectedVoiceName } = settingsState;
  const selectionComplete = isAudioSelectionComplete(selectedProviderId, selectedModel, selectedVoiceName);

  let voice = EMPTY_VOICE;
  if (selectionComplete) {
    voice = findVoiceInConfig(audioProviderConfig, selectedProviderId, selectedModel, selectedVoiceName) || EMPTY_VOICE;
  }

  const valid = !audioEnabled || Boolean(voice.name);
  return { voice, valid };
};

/**
 * Builds merged otherParams from Advanced Settings state for graph save.
 * @param {object} settingsState
 * @param {Array} audioProviderConfig
 * @returns {object|null} null when voice resolution fails for enabled audio
 */
export const buildMergedOtherParams = (
  settingsState,
  audioProviderConfig,
  tenantMemorySaverEnabled = false,
  tenantAgentThinkingEnabled = false
) => {
  const jsonResult = utils.validateJsonObject(settingsState.jsonText);
  if (!jsonResult.valid) {
    return null;
  }
  const additionalParams = jsonResult.parsed;

  const graphMemorySaver = getGraphMemorySaverFromParams(additionalParams);
  const memorySaverValue = resolveMemorySaverSaveValue(
    tenantMemorySaverEnabled,
    graphMemorySaver,
    settingsState.enableMemorySaver
  );
  const graphAgentThinking = getGraphAgentThinkingFromParams(additionalParams);
  const agentThinkingValue = resolveAgentThinkingSaveValue(
    tenantAgentThinkingEnabled,
    graphAgentThinking,
    settingsState.enableAgentThinkingMsg
  );

  if (!settingsState.audioEnabled) {
    return {
      ...stripEnableAgentThinkingMsg(
        stripEnableMemorySaver(mergeAudioIntoParams(additionalParams, false, '', '', EMPTY_VOICE))
      ),
      [ENABLE_MEMORY_SAVER_KEY]: memorySaverValue,
      [ENABLE_AGENT_THINKING_MSG_KEY]: agentThinkingValue
    };
  }

  const { voice, valid } = resolveVoiceForSave(settingsState, audioProviderConfig);
  if (!valid) {
    return null;
  }

  const paramsWithAudio = mergeAudioIntoParams(
    additionalParams,
    settingsState.audioEnabled,
    settingsState.selectedProviderId,
    settingsState.selectedModel,
    voice
  );

  return {
    ...stripEnableAgentThinkingMsg(stripEnableMemorySaver(paramsWithAudio)),
    [ENABLE_MEMORY_SAVER_KEY]: memorySaverValue,
    [ENABLE_AGENT_THINKING_MSG_KEY]: agentThinkingValue
  };
};
